import { BigNumber, ethers } from "ethers";
import { getRpcUrls } from "../../config/chains";
import { ChainIdEnum } from "../../enums/chain";

/**
 * On-chain reads behind the onboarding balance check. The trading balance used
 * to come from `GET /account?account=` without an API key; the backend now
 * requires API-key auth on account routes, and an onboarding user has no key
 * yet by definition. The vault is the source of truth anyway, so it is read
 * directly: `getTotalBalance(user)` exists on both the UnifiedVault and the
 * BalanceManagerVault ABIs.
 */
export const VAULT_TOTAL_BALANCE_ABI = [
  "function getTotalBalance(address user) external view returns (uint256)",
];

type TotalBalanceReader = { getTotalBalance: (user: string) => Promise<BigNumber> };
export type ConnectVault = (
  address: string,
  provider: ethers.providers.Provider
) => TotalBalanceReader;

const connectVault: ConnectVault = (address, provider) =>
  new ethers.Contract(address, VAULT_TOTAL_BALANCE_ABI, provider) as unknown as TotalBalanceReader;

export const getReadProviders = (
  provider: ethers.providers.Provider | undefined,
  chainId?: ChainIdEnum
): ethers.providers.Provider[] => {
  const rpcProviders = Array.from(new Set(getRpcUrls(chainId).filter(Boolean))).map(
    (rpcUrl) => new ethers.providers.JsonRpcProvider(rpcUrl)
  );
  return provider ? [...rpcProviders, provider] : rpcProviders;
};

/** Try each provider in turn; throw the last error only if all fail. */
export const readWithFallback = async <T,>(
  providers: ethers.providers.Provider[],
  read: (provider: ethers.providers.Provider) => Promise<T>
): Promise<T> => {
  let lastError: unknown;
  for (const readProvider of providers) {
    try {
      return await read(readProvider);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error("No provider available for balance read");
};

/**
 * The account's total balance in the trading vault, in whole collateral units.
 * `undefined` when the chain has no vault configured (nothing to read).
 */
export const readVaultTotalBalance = async (
  providers: ethers.providers.Provider[],
  vaultAddress: string | undefined,
  account: string,
  decimals: number,
  connect: ConnectVault = connectVault
): Promise<number | undefined> => {
  if (!vaultAddress) return undefined;
  const raw = await readWithFallback(providers, (p) =>
    connect(vaultAddress, p).getTotalBalance(account)
  );
  return Number(ethers.utils.formatUnits(raw, decimals));
};
